import {EventEmitter, Injectable, Output} from '@angular/core';
import {PlaybackQuality, Tour, TourVideoType} from '../model/tour';
import {Encoding} from '../model/encoding';
import {Scene} from '../model/scene';
import {TrackingService} from './tracking.service';
import {TourTag} from '../model/tour-tag';
import {Title} from '@angular/platform-browser';

@Injectable()
export class TourService {

  hostProtocol = '';
  tour: Tour = null;
  currentScene: Scene;
  optionsAvailable = false;
  videoAvailable = false;
  web360Available = false;
  web360Visibile = false;
  web360OptionsButtonVisible = false;
  web360OptionsVisible = false;
  currentVideoTime = 0;
  maxStars = 5;
  resumeVideoFromCurrentTime = false;
  tourTag: TourTag;
  @Output() sceneChanged = new EventEmitter<boolean>();
  @Output() videoVisibilityChanged = new EventEmitter<boolean>();
  @Output() web360OptionsVisibilityChanged = new EventEmitter<boolean>();


  constructor(private trackingService: TrackingService, private titleService: Title) {
    this.hostProtocol = window.location.protocol + '//';  // TO DO:  DON'T COMMIT 'https://'
  }

  configureDisplayForTour(tour: Tour) {
    this.tour = tour;
    this.videoAvailable = tour.hasVideoTours();
    // reset UI booleans
    this.web360Available = false;
    this.web360Visibile = false;
    this.web360OptionsButtonVisible = false;
    this.optionsAvailable = false;
  }

  getStarsStringForRating(rating: number) {
    const emptyStarText = '';
    const halfStarText = '';
    const fullStarText = '';

    let fullStars       = Math.floor(rating);
    let emptyStars      = Math.floor(this.maxStars - rating);
    const partialStars  = this.maxStars - (fullStars + emptyStars);
    let starString = '';
    while (fullStars > 0) {
      starString = starString + fullStarText;
      fullStars -= 1;
    }
    if (partialStars > 0) {
      starString = starString + halfStarText;
    }
    while (emptyStars > 0) {
      starString = starString + emptyStarText;
      emptyStars -= 1;
    }
    return starString;
  }

  getSourcesArrayForEncodings(encodings: Encoding[]) {
    const sources: Array<Object> = [];
    for (const e of encodings) {
      sources.push(
        {
          src: this.tour.tourVideo.httpPath + e.relUrl,
          type: 'video/mp4'
        }
      );
    }
    return sources;
  }

  getTourVideoSourceArrayForPreferredFloorplan() {
    const encodings: Encoding[] = this.getTourVideoEncodingsForPreferredFloorplan();
    return this.getSourcesArrayForEncodings(encodings);
  }

  getTourVideoEncodingsForPreferredFloorplan() {
    return (this.tour.preferredPlaybackType === TourVideoType.Floorplan)
            ? this.tour.tourVideo.encodings : this.tour.tourVideo.fullFrameEncodings;
  }

  getSceneLabels(ingoreScenesWithoutOptions: boolean) {
    const sceneLabels: string[] = [];
    for (const scene of this.tour.sceneList) {
      // don't exclude or exclude and has options
      if (!ingoreScenesWithoutOptions || (ingoreScenesWithoutOptions && scene.hasOptionsToDisplay())) {
        sceneLabels.push(scene.displayName);
      }
    }
    return sceneLabels;
  }

  showWeb360(show: boolean) {
    this.web360Visibile = show;
    this.web360OptionsVisible = false;
    this.web360OptionsButtonVisible = (this.web360Visibile && this.optionsAvailable);
    this.web360OptionsVisibilityChanged.emit(this.web360OptionsVisible);
    if (show) {
      this.trackingService.trackAction((this.tour.hasGeneric360())
        ? TrackingService.kActionVideo3D : TrackingService.kActionCrosslink, this.tour.crosslink.url, 0);
    }
  }

  showWeb360Options(show: boolean) {
    this.web360OptionsVisible = show;
    this.web360OptionsVisibilityChanged.emit(this.web360OptionsVisible);
  }

  updateCurrentScene(time: number) {
    if (time === 0) {
      this.currentScene = null;
      this.sceneChanged.emit(true);
    } else if (!this.currentScene ||
        (this.currentScene.startTime > time || this.currentScene.endTime < time)) {
      this.currentVideoTime = time;
      const scene = this.tour.findSceneForCurrentTime(time);
      if (scene && (!this.currentScene || (scene.id !== this.currentScene.id))) {
        this.currentScene = scene;
        this.sceneChanged.emit(true);
      }
    }
  }

  updateCurrentSceneForName(name: string) {
    if (name === null) {
      this.currentScene = null;
      this.sceneChanged.emit(true);
    } else  {
      const scene = this.tour.findSceneByDisplayName(name);
      if (scene && (!this.currentScene || (scene.id !== this.currentScene.id))) {
        this.currentScene = scene;
        this.sceneChanged.emit(true);
      }
    }
  }


  tourFeatureEnabled(feature) {
    if (this.tour) {
      if (feature === 'about') {
        return this.tour.features.about.enabled;
      }

      if (feature === 'community') {
        return this.tour.features.community.enabled;
      }

      if (feature === 'contacts') {
        return this.tour.features.contacts.enabled;
      }

      if (feature === 'feedback') {
        return this.tour.features.feedback.enabled;
      }

      if (feature === 'wishlist') {
        return this.tour.features.wishlist.enabled;
      }
    }
    return false;
  }

  tourFeatureTitle(feature) {
    if (feature === 'about') {
      return this.tour.features.about.label;
    }

    if (feature === 'community') {
      return this.tour.features.community.label;
    }

    if (feature === 'contacts') {
      return this.tour.features.contacts.label;
    }

    if (feature === 'feedback') {
      return this.tour.features.feedback.label;
    }

    if (feature === 'wishlist') {
      return this.tour.features.wishlist.label;
    }

    if (feature === 'tour') {
      return 'Video Tour';
    }

    if (feature === 'features') {
      return 'Features Explorer';
    }

    if (feature === '3d') {
      return '3D Showcase';
    }
    return '';
  }

  setDocumentTitleWithTour(pageName: string) {
    let title = 'GoTour';
    if (this.tour !== null) {
      title = title + ' - ' + this.tour.modelInfo.builder + ': ' + this.tour.modelInfo.modelName;
    }
    title = (pageName !== null) ? (title + ' - ' + pageName) : title;
    return this.titleService.setTitle(title);
  }

  calculateBandwidth(encodings: Encoding[]) {
    const bandwidthImageSize = 180961;
    const bandwidthImageUrl = 'https://cdn.doorjam.com/bwtest1.jpg.optimized.jpg' + '?n=' + Math.random();
    let startTime, endTime;
    const self = this;
    const download = new Image();
    download.onload = function () {
      endTime = (new Date()).getTime();
      self.tour.preferredPlaybackQuality = getInitialPlaybackQuality(encodings);
    };
    startTime = (new Date()).getTime();
    download.src = bandwidthImageUrl;

    function getInitialPlaybackQuality(encodingsArray: Encoding[]): PlaybackQuality {
      const duration = (endTime - startTime) / 1000;
      const bitsLoaded = bandwidthImageSize * 8;
      const speedBps = (bitsLoaded / duration);
      const speedKbps = (speedBps / 1024); // .toFixed(2);

      encodingsArray.sort(function(a: Encoding, b: Encoding) {
        return b.minKbps - a.minKbps;
      });

      for (const e of encodings) {
        if (e.minKbps < speedKbps) {
          if (e.qualityLevelName === PlaybackQuality.HD.toString()) {
            return PlaybackQuality.HD;
          } else if (e.qualityLevelName === PlaybackQuality.High.toString()) {
            return PlaybackQuality.High;
          } else if (e.qualityLevelName === PlaybackQuality.Medium.toString()) {
            return PlaybackQuality.Medium;
          }

          return PlaybackQuality.Low;
        }
      }

      // const speedMbps = (speedKbps / 1024); // .toFixed(2);
      // alert('Your connection speed is: \n' +
      //   speedBps + ' bps\n' +
      //   speedKbps + ' kbps\n' +
      //   speedMbps + ' Mbps\n' );
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/services/tour.service.ts